import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import KeyedItem from "../models/base/item";
import { CrudServiceType } from "../services/crudService";
import { AppItemRestServicesType } from "../services/appServices";
import { useItemService } from "./useServices";
import { Pagination, PaginationQuery } from "../models/base/Pagination";
import { restError } from "@/models/client/RestResponse";

export type ItemsFunctions<T extends Partial<KeyedItem>> = {
  /** Stays idle until `id` is known: no request goes out for a missing id. */
  useQuerySingle: (
    id: string | null | undefined
  ) => UseQueryResult<T | undefined, unknown>;
  /* useQueryMany: () => UseQueryResult<T[], string>; */
  useQueryPagination: (
    params: PaginationQuery,
    signal?: AbortSignal,
    options?: Omit<
      UseQueryOptions<Pagination<T>, Error>,
      "queryKey" | "queryFn"
    >
  ) => UseQueryResult<Pagination<T>, Error>;
  create: UseMutationResult<unknown, unknown, Partial<T>, unknown>;
  update: UseMutationResult<unknown, unknown, Partial<T>, unknown>;
  remove: UseMutationResult<unknown, unknown, string, unknown>;
};

export const useItemsCrud = <T extends Partial<KeyedItem>>(
  key: string[],
  selector: (services: AppItemRestServicesType) => CrudServiceType<T>
): ItemsFunctions<T> => {
  /**** Hooks ****/
  const queryClient = useQueryClient();

  /**** Services ****/
  const service = useItemService(selector);

  /**** Queries & Mutations ****/

  // Queries item by id. Providers mounted on every page (the upload hub) and
  // pages that read the id from the route call this before the id is known;
  // an empty id would be fetched as `GET /<resource>/` and answered 400.
  const useQuerySingle = (id: string | null | undefined) =>
    useQuery<T | undefined, unknown, T | undefined>({
      queryKey: [...key, id],
      queryFn: async () => {
        if (!id) throw new Error("An id is required to query a single item");
        const response = await service.querySingle(id);
        if (response.success) return response.value;
        else throw restError(response);
      },
      enabled: Boolean(id),
    });

  // Query pagination
  const useQueryPagination = (
    params: PaginationQuery,
    signal?: AbortSignal,
    options?: Omit<
      UseQueryOptions<Pagination<T>, Error>,
      "queryKey" | "queryFn"
    >
  ) =>
    useQuery<Pagination<T>, Error>({
      queryKey: [
        ...key,
        "pagination",
        params.pageNumber,
        params.pageSize,
        params.search,
        params.sortBy,
        params.sortAscending,
        params.filters,
      ],
      queryFn: async () => {
        const response = await service.queryPagination(params, signal);
        if (response.success) return response.value;
        else throw restError(response);
      },
      ...options,
    });

  //Query many
  /* const useQueryMany = () =>
    useQuery<T[], string>({
      queryKey: key,
      queryFn: async () => {
        const response = await service.queryMany();
        if (response.success) return response.value;
        else throw restError(response);
      },
    }); */

  // Creates item
  const create = useMutation({
    mutationKey: [...key, "create"],
    mutationFn: async (request: Partial<T>) => {
      const response = await service.create(request);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key, exact: false });
    },
  });

  // Updates item
  const update = useMutation({
    mutationKey: [...key, "update"],
    mutationFn: async (request: Partial<T>) => {
      const response = await service.update(request);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: key,
        exact: false,
      });
    },
  });

  //Removes Item
  const remove = useMutation({
    mutationKey: [...key, "remove"],
    mutationFn: async (id: string) => {
      const response = await service.delete(id);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
  });

  /**** Value ****/
  return {
    useQuerySingle,
    useQueryPagination,
    /*  useQueryMany, */
    create,
    update,
    remove,
  };
};
